<script setup lang="ts">
import DeletePolygon from 'assets/icons/delete-polygon.svg'
import Polygon from 'assets/icons/polygon.svg'
import Pan from 'assets/icons/fit-to-window.svg'
import Pin from 'assets/icons/pin.svg'
import { useElementVisibility } from '@vueuse/core'

const props = defineProps({
    googleMapsConfig: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    modelValue: {
        type: Object,
        required: false,
        default: () => ({}),
    }
})

// Load i18n
const { t, locale } = useI18n()

// Load config
const config = useRuntimeConfig()

// Define error alert
const errorAlert = useState('error')

// Define initial geolocation (center of Germany)
const lng = 10.4515
const lat = 51.165

// Component-scoped variables
let map = null
let polygons = []

// Define some refs
const mapContainer = ref(null)
const mapDiv = ref(null)
const draggableMarker = ref(null)
const firstUserInteraction = ref(true)
const stopWatcher = ref(false)
const lastZoom = ref('')
const isPendingMap = ref(false)
const geoLocationValue = ref()
const polygonCoordinates = ref<[number, number][]>([])
const deletePolygon = ref(false)
const enablePolygonDrawing = ref(false)
const initCustomPanTo = ref(false)
const isPanActive = ref(false)
const initLng = ref(lng)
const initLat = ref(lat)
const initSetLocation = ref(false)
const autoDetection = ref(false)
const street = ref('')
const streetNr = ref('')
const city = ref('')
const zipCode = ref('')

// Detect geolocation if map is in viewport
const targetIsVisible = useElementVisibility(mapContainer)

// Define Google Maps API url
const googleMapsApi = computed(() => `https://maps.googleapis.com/maps/api/js?key=${config?.public?.googleMapsApiKey}&loading=async&region=${locale.value?.toUpperCase()}&language=${locale.value}&callback=initMap`)

// Define cookie consent preferences
const cookieConsent = computed(() => {
    if (import.meta.client) {
        return Cookiebot?.consent
    }

    return false
})

/**
 *  Load an external script
 *  @param id
 *  @param src
 *  @returns Promise
 */
const loadExternalScript = (id: string, src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const existingScript = document.getElementById(id)
        if (existingScript) {
            // Script already exists; trigger the callback manually
            if (typeof window.initMap === 'function') {
                window.initMap()
            }
            resolve()
            return
        }

        const script = document.createElement('script')
        script.src = src
        script.async = true
        script.defer = true
        script.id = id
        script.type = 'text/javascript'
        script.setAttribute('data-cookieconsent', 'Preferences')
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Failed to load the script'))
        document.head.appendChild(script)
    })
}

/**
 * Set GeoLocation
 * @param value
 */
const setGeoLocation = (value) => {
    geoLocationValue.value = value
}

/**
 * Suppress form submit
 * @param event
 */
const suppressFormSubmit = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
        event.preventDefault()
        initSetLocation.value = true
    }
}

/**
 *  Auto detection
 * @param lat
 * @param lng
 */
const initAutoDetection = (lat: number, lng: number) => {
    initLat.value = lat
    initLng.value = lng
    autoDetection.value = true
    errorAlert.value = null
    isPendingMap.value = false
}

/**
 *  Set initial location
 */
const setInitialLocation = () => {
    // Do nothing if user has not agreed to load google map
    if (!cookieConsent.value?.preferences) return

    // Set pending state
    isPendingMap.value = true

    // Check if user has entered valid data before (add additional lost child...)
    if (props?.googleMapsConfig?.savedPosition) {
        initAutoDetection(
            props.googleMapsConfig?.savedPosition?.coordinates[1],
            props.googleMapsConfig?.savedPosition?.coordinates[0]
        )
    } else {
        // Check if geolocation feature is available
        if (navigator?.geolocation) {
            // Get current position and invoke auto detection
            navigator?.geolocation?.getCurrentPosition(
                (position) => {
                    initAutoDetection(
                        position.coords.latitude,
                        position.coords.longitude
                    )
                },
                // Handle error
                (error) => {
                    console.log('Error getting location: ', error)

                    // Stop pending state
                    isPendingMap.value = false
                }
            )
        } else {
            // Handle error
            console.log('Geolocation is not supported by this browser.')

            // Stop pending state
            isPendingMap.value = false
        }
    }

    // Stop the watcher after the first trigger
    stopWatcher.value = true
}

/*
 * Handle draw polygon
 */
const handleDrawPolygon = async () => {
    firstUserInteraction.value = false

    if (polygonCoordinates.value?.length < 1) {
        // Toggle polygon drawing button
        enablePolygonDrawing.value = !enablePolygonDrawing.value
    } else {
        // Button is active, delete polygon
        deletePolygon.value = true

        await nextTick()

        const scrollElement = mapDiv.value?.getBoundingClientRect()
        const distance = scrollElement.top + window.scrollY - 140

        // Scroll to target
        window.scrollTo({
            top: distance,
            behavior: 'smooth'
        })
    }
}

/**
 *  Function to delete draggable marker
 */
const deleteDraggableMarker = () => {
    if (!draggableMarker.value) return
    draggableMarker.value.map = null
    draggableMarker.value.setMap(null)
    google.maps.event.clearInstanceListeners(draggableMarker.value)
    draggableMarker.value = null
}

/*
 * Handle custom pan
 */
const handleCustomPanTo = () => {
    initCustomPanTo.value = true
}

/**
 * Enable the Cookiebot consent preferences
 */
const enableMarketingPreferences = () => {
    Cookiebot.submitCustomConsent(true, false, false)

    setInitialLocation()
}

/**
 * Handle Google Maps loading on accept
 */
const handleCookiebotAccept = async () => {
    if (Cookiebot?.consent?.preferences) {
        await loadExternalScript('google-map-api', googleMapsApi.value)
    }
}

// Compute class of the draw polygon button
const getDrawPolygonClass = computed(() => {
    return {
        'draw-polygon-cta-active': enablePolygonDrawing.value,
        'delete-polygon-cta': polygonCoordinates.value?.length > 1,
        'animated-draw-polygon-cta': firstUserInteraction.value
    }
})

// Compute label of the draw polygon button
const getDrawPolygonLabel = computed(() => {
    if (enablePolygonDrawing.value) {
        return t('form.form_last_seen_geometry_enabled_label')
    } else if (polygonCoordinates.value?.length > 1) {
        return t('form.form_last_seen_geometry_delete_label')
    } else {
        return t('form.form_last_seen_geometry_enable_label')
    }
})

// Compute address object
const addressObject = computed(() => {
    const rawAddress = {
        street: street.value,
        streetNr: streetNr.value,
        zipCode: zipCode.value,
        city: city.value,
        lat: draggableMarker.value ? initLat.value : null, // Accept user value, not initial value!
        lng: draggableMarker.value ? initLng.value : null, // Accept user value, not initial value!
    }

    // Filter out keys with null or empty string values
    return Object.fromEntries(
        Object.entries(rawAddress).filter(
            ([_, value]) => value !== null && value !== '' && value !== undefined
        )
    )
})

// Emit value to parent component when either geoLocationValue or polygonCoordinates changes
watch(() => [addressObject.value, polygonCoordinates.value], (newValues, oldValues) => {
    if (newValues[0] !== oldValues[0] || newValues[1] !== oldValues[1]) {
        const emittedLastSeenAddress =
            newValues[0] && Object.keys(newValues[0])?.length > 1 ?
                    {
                        lastSeenAddress: {
                            street: newValues[0].streetNr ? `${newValues[0].street} ${newValues[0].streetNr}` : newValues[0].street,
                            city: newValues[0].city,
                            zip: newValues[0].zipCode,
                            position: {
                                coordinates: (newValues[0].lng && newValues[0].lat)
                                    ? [
                                            newValues[0].lng,
                                            newValues[0].lat
                                        ]
                                    : [],
                                type: 'Point'
                            }
                        }
                    }
                : {}

        const emittedLastSeenGeometry =
            newValues[1] && newValues[1]?.length > 1 ?
                    { lastSeenGeometry: {
                        coordinates: [newValues[1]],
                        type: 'Polygon'
                    },
                    }
                : {}

        const emittedValue = {
            ...emittedLastSeenGeometry,
            ...emittedLastSeenAddress
        }

        emit('update:modelValue', emittedValue)
    }
})

/**
 *  Initialize the map
 */
if (import.meta.client) {
    // Callback from Google Maps api
    window.initMap = async () => {
        // Import libraries
        const { Map } = await google.maps.importLibrary('maps')
        const { AdvancedMarkerElement } = await google.maps.importLibrary('marker')
        const { Geocoder } = await google.maps.importLibrary('geocoding')

        // Define svg Marker
        const parser = new DOMParser()
        const pinSvgString = `<svg width="100" height="100" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none">
            <!-- Define the filter with a drop shadow -->
            <filter id="shadow">
                <feDropShadow dx="0.5" dy="0.5" stdDeviation="1" flood-color="black" flood-opacity="0.2" />
            </filter>

            <!-- Apply the filter to the path -->
            <path fill="rgba(61, 171, 226, 0.9)" fill-rule="evenodd" d="M11.291 21.706 12 21l-.709.706zM12 21l.708.706a1 1 0 0 1-1.417 0l-.006-.007-.017-.017-.062-.063a47.708 47.708 0 0 1-1.04-1.106 49.562 49.562 0 0 1-2.456-2.908c-.892-1.15-1.804-2.45-2.497-3.734C4.535 12.612 4 11.248 4 10c0-4.539 3.592-8 8-8 4.408 0 8 3.461 8 8 0 1.248-.535 2.612-1.213 3.87-.693 1.286-1.604 2.585-2.497 3.735a49.583 49.583 0 0 1-3.496 4.014l-.062.063-.017.017-.006.006L12 21zm0-8a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" clip-rule="evenodd" filter="url(#shadow)"/>
        </svg>`

        // Parse svg
        const pinSvg = parser.parseFromString(pinSvgString, 'image/svg+xml').documentElement

        // Define map config
        const mapConfig = {
            center: { lat: initLat.value, lng: initLng.value },
            zoom: 15,
            mapId: 'map1',
            minZoom: 2,
            zoomControl: true,
            zoomControlOptions: {
                position: google.maps.ControlPosition.RIGHT_CENTER
            },
            mapTypeControl: true,
            mapTypeControlOptions: {
                position: google.maps.ControlPosition.LEFT_BOTTOM
            },
            fullscreenControl: false,
            streetViewControl: false,
            language: locale.value,
        }

        // Init map
        const mapElement = document.getElementById('map')
        map = new Map(mapElement, mapConfig)

        // Check if map already exists
        if (map) {
            // If the map is already initialized, just set the new language
            map.setOptions({
                language: locale.value
            })
        }

        // Init geocoder
        const geocoder = new Geocoder()

        // Function to create and return a new marker
        const createDraggableMarker = () => {
            // Init advanced marker
            const newDraggableMarker = new AdvancedMarkerElement({
                map,
                position: { lat: initLat.value, lng: initLng.value },
                gmpDraggable: true,
                content: pinSvg
            })

            // Init reverse geocode by dragging marker
            newDraggableMarker?.addListener('dragend', () => {
                const position = newDraggableMarker?.position

                initLat.value = position.lat
                initLng.value = position.lng

                const location = {
                    lat: initLat.value,
                    lng: initLng.value
                }

                geocoder.geocode({ location: location, language: locale.value }, (results, status) => {
                    if (status === 'OK' && results[0]) {
                        setFormattedAddress(results[0].address_components)
                    } else {
                        console.log('Geocode was not successful for the following reason: ' + status)
                    }
                })

                isPanActive.value = true
            })

            return newDraggableMarker
        }

        // Polygon style options (shared by user-drawn and restored polygons)
        const polygonStyleOptions = {
            editable: true,
            clickable: true,
            draggable: true,
            strokeColor: '#00b2fb',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#00b2fb',
            fillOpacity: 0.28
        }

        // Custom polygon drawing state (replaces deprecated google.maps.drawing library)
        let drawPoints = []
        let previewPolyline = null
        let vertexMarkers = []
        let drawingClickListener = null

        // Start interactive polygon drawing
        const startPolygonDrawing = () => {
            if (drawingClickListener) return // already drawing

            drawPoints = []

            previewPolyline = new google.maps.Polyline({
                map,
                path: [],
                strokeColor: polygonStyleOptions.strokeColor,
                strokeOpacity: polygonStyleOptions.strokeOpacity,
                strokeWeight: polygonStyleOptions.strokeWeight,
                clickable: false
            })

            map.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true })

            drawingClickListener = map.addListener('click', (event) => {
                if (!event?.latLng) return
                addPolygonVertex(event.latLng)
            })
        }

        // Add a vertex to the in-progress polygon
        const addPolygonVertex = (latLng) => {
            drawPoints.push(latLng)
            previewPolyline?.setPath(drawPoints)

            const isFirst = vertexMarkers.length === 0
            const dot = document.createElement('div')
            dot.className = isFirst ? 'polygon-vertex polygon-vertex-first' : 'polygon-vertex'

            const marker = new AdvancedMarkerElement({
                map,
                position: latLng,
                content: dot,
                gmpClickable: isFirst,
                zIndex: 1000
            })

            // Clicking the first vertex closes the polygon (needs >= 3 points)
            if (isFirst) {
                marker.addListener('gmp-click', () => {
                    if (drawPoints.length < 3) return
                    finishPolygonDrawing()
                })
            }

            vertexMarkers.push(marker)
        }

        // Tear down all drawing artifacts (preview, markers, listener, cursor)
        const cleanupDrawingArtifacts = () => {
            previewPolyline?.setMap(null)
            previewPolyline = null

            vertexMarkers.forEach((marker) => {
                google.maps.event.clearInstanceListeners(marker)
                marker.map = null
            })
            vertexMarkers = []

            drawingClickListener?.remove()
            drawingClickListener = null

            map.setOptions({ draggableCursor: null, disableDoubleClickZoom: false })

            drawPoints = []
        }

        // Convert collected points into a final polygon and hand off to createPolygon
        const finishPolygonDrawing = () => {
            const points = drawPoints.slice()
            cleanupDrawingArtifacts()

            const polygon = new google.maps.Polygon({ paths: points })
            polygon.setMap(map)
            polygon.setOptions({
                ...polygonStyleOptions,
                zIndex: 1000
            })

            createPolygon(polygon)
        }

        /**
         *  Create a new polygon
         * @param newPolygon
         */
        const createPolygon = (newPolygon) => {
            // Store the new polygon
            polygons.push(newPolygon)

            // Update the polygon coordinates
            updatePolygonCoords(newPolygon)

            // Disable polygon drawing toggle (watcher tears down any remaining drawing state)
            enablePolygonDrawing.value = false

            // Ensure polygon is draggable and editable
            newPolygon.setOptions({
                draggable: true,
                editable: true,
                clickable: true,
                zIndex: 1000,
            })

            const path = newPolygon.getPath()

            google.maps.event.addListener(newPolygon, 'dragend', () => updatePolygonCoords(newPolygon))
            google.maps.event.addListener(path, 'insert_at', () => updatePolygonCoords(newPolygon))
            google.maps.event.addListener(path, 'remove_at', () => updatePolygonCoords(newPolygon))
            google.maps.event.addListener(path, 'set_at', () => updatePolygonCoords(newPolygon))
        }

        /**
         * Update the polygon coordinates
         * @param {google.maps.Polygon} polygon
         */
        const updatePolygonCoords = (polygon) => {
            const path = polygon.getPath()

            const coordinates = Array.from(path.getArray(), latLng => [latLng?.lng(), latLng?.lat()])

            // Ensure the polygon is closed by making the first and last coordinates the same
            if (coordinates.length > 0) {
                const firstCoordinate = coordinates[0]
                const lastCoordinate = coordinates[coordinates.length - 1]

                // Add the first coordinate to the end if it's not already the same as the last one
                if (firstCoordinate[0] !== lastCoordinate[0] || firstCoordinate[1] !== lastCoordinate[1]) {
                    coordinates.push(firstCoordinate)
                }
            }

            polygonCoordinates.value = coordinates

            isPanActive.value = true
        }

        /**
         *  Create and add the polygon to the map using the saved coordinates
         *  @param coordinates
         */
        const createPolygonFromCoordinates = (coordinates) => {
            const newPolygon = new google.maps.Polygon({
                paths: coordinates,
                ...polygonStyleOptions
            })

            // Add polygon to map (programmatically)
            newPolygon.setMap(map)

            // Ensure polygon is draggable and editable
            newPolygon.setOptions({
                draggable: true,
                editable: true,
                clickable: true,
                zIndex: 1000,
            })

            // Create the polygon
            createPolygon(newPolygon)
        }

        // Create and add the polygon to the map using the saved coordinates
        if (props.googleMapsConfig?.savedGeometry) {
            if (props.googleMapsConfig?.savedGeometry[0]?.length > 0) {
                // Get saved coordinates
                const savedCoordinates = props.googleMapsConfig?.savedGeometry[0].map(coord => ({ lng: coord[0], lat: coord[1] }))

                // If there are coordinates, create the polygon programmatically
                createPolygonFromCoordinates(savedCoordinates)

                // Init custom pan
                initCustomPanTo.value = true
            }
        }

        /*
        * Function to set marker and center
        */
        const setMarkerAndCenter = () => {
            const location = {
                lat: initLat.value,
                lng: initLng.value
            }

            geocoder.geocode({ location: location, language: locale.value }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    setFormattedAddress(results[0].address_components)

                    // Set the map center and Marker after a short delay
                    setTimeout(() => {
                        // Delete existing marker
                        deleteDraggableMarker()

                        // Now set the marker
                        draggableMarker.value = createDraggableMarker()

                        // Set the map center
                        map.setCenter({ lat: initLat.value, lng: initLng.value })
                    }, 500)
                } else {
                    console.log('Geocode was not successful for the following reason: ' + status)
                }
            })
        }

        /*
         * Function to set formatted address
         *
         * @param addressComponents
         */
        const setFormattedAddress = (addressComponents) => {
            // Reset all address values before processing the components
            street.value = ''
            streetNr.value = ''
            city.value = ''
            zipCode.value = ''

            // Iterate over address components to extract address details
            addressComponents.forEach((component) => {
                const { types, long_name } = component

                if (types.includes('route')) street.value = long_name
                if (types.includes('street_number')) streetNr.value = long_name
                if (types.includes('locality')) city.value = long_name
                if (types.includes('postal_code')) zipCode.value = long_name
            })

            // Format the geoLocationValue with conditional spaces and commas
            const formattedAddress = [
                street.value ? `${street.value}` : '',
                streetNr.value ? `${streetNr.value}` : '',
                zipCode.value ? `${zipCode.value}` : '',
                city.value ? `${city.value}` : ''
            ]
                .filter(part => part) // Remove empty parts
                .join(' ') // Join with space

            // Set the final geoLocationValue
            geoLocationValue.value = formattedAddress ? `${formattedAddress} (${initLat.value} ${initLng.value})` : `${initLat.value} ${initLng.value}`
        }

        // User clicks on map to set marker manually
        map.addListener('click', (event) => {
            // Skip marker placement while user is drawing a polygon
            if (enablePolygonDrawing.value) return

            initLng.value = event.latLng?.lng()
            initLat.value = event.latLng?.lat()

            setMarkerAndCenter()
        })

        // Zoom map was changed, either by the user or programmatically
        map.addListener('zoom_changed', () => {
            isPanActive.value = lastZoom.value !== map.getZoom()
        })

        // The map has been moved by the user
        map.addListener('dragend', () => {
            isPanActive.value = true
        })

        // User has allowed to track location
        watch(() => [autoDetection.value], () => {
            if (autoDetection.value) {
                setMarkerAndCenter()

                autoDetection.value = false
            }
        }, { immediate: true })

        // User starts or cancels polygon drawing
        watch(() => [enablePolygonDrawing.value], () => {
            if (enablePolygonDrawing.value) {
                startPolygonDrawing()
            } else {
                cleanupDrawingArtifacts()
            }
        })

        // User has clicked on delete polygon button
        watch(() => [deletePolygon.value], () => {
            if (deletePolygon.value) {
                // Delete all existing polygons
                polygons.forEach((polygon) => {
                    polygon.setMap(null)
                })

                polygons = [] // Clear the array
                polygonCoordinates.value = []

                // Reset this value
                deletePolygon.value = false

                // Activate custom pan
                isPanActive.value = true
            }
        })

        // User has clicked on go or pressed enter
        watch(() => [initSetLocation.value], () => {
            if (initSetLocation.value) {
                const address = geoLocationValue.value

                geocoder.geocode({ address, language: locale.value }, (results, status) => {
                    if (status === 'OK' && results[0]) {
                        const location = results[0].geometry.location
                        initLng.value = location.lng()
                        initLat.value = location.lat()

                        // Set the map center
                        map.setCenter({ lat: initLat.value, lng: initLng.value })

                        // Delete existing marker
                        deleteDraggableMarker()

                        // Now set the marker
                        draggableMarker.value = createDraggableMarker()

                        // Set the geolocation value
                        setFormattedAddress(results[0].address_components)

                        // Reset error
                        errorAlert.value = null
                    } else {
                        console.log('Geocode was not successful for the following reason: ' + status)

                        errorAlert.value = {
                            type: 'location',
                            label: `${t('form.form_maps_error')}`
                        }
                    }

                    initSetLocation.value = false
                })
            }
        })

        // User has deleted the geolocation input
        watch(() => [geoLocationValue.value], () => {
            if (!geoLocationValue.value && draggableMarker.value) {
                deleteDraggableMarker()

                street.value = ''
                streetNr.value = ''
                zipCode.value = ''
                city.value = ''
                initLat.value = 0
                initLng.value = 0

                // Activate custom pan
                isPanActive.value = true
            }
        })

        // User inits custom pan
        watch(() => [initCustomPanTo.value], () => {
            if (initCustomPanTo.value) {
                // If there are no polygons or marker, set the map center
                if (!draggableMarker.value && polygons.length < 1) {
                    initLat.value = lat
                    initLng.value = lng

                    map.setCenter({ lat: initLat.value, lng: initLng.value })
                    map.setZoom(15)
                } else {
                    // Create a bounds object
                    const bounds = new google.maps.LatLngBounds()

                    if (draggableMarker.value) {
                        // Extend bounds to include the marker's position
                        bounds.extend(draggableMarker.value?.position)
                    }

                    if (polygons.length > 0) {
                        // Extend bounds to include each point in the polygon
                        polygons.forEach((polygon) => {
                            polygon.getPath().forEach((coord) => {
                                bounds.extend(coord)
                            })
                        })
                    }

                    // Adjust the map to fit the bounds
                    map?.fitBounds(bounds)

                    // Optionally, you can set a minimum zoom level
                    const listener = google.maps.event.addListener(map, 'idle', function () {
                        if (map.getZoom() > 15) map.setZoom(15)

                        lastZoom.value = map.getZoom()
                        google.maps.event.removeListener(listener)
                    })
                }

                initCustomPanTo.value = false
                isPanActive.value = false
            }
        }, { immediate: true })
    }
}

// Init google map
onMounted(async () => {
    // Load Google Maps instantly if user has already accepted preferences
    if (import.meta.client && mapDiv.value && Cookiebot?.consent?.preferences) {
        await loadExternalScript('google-map-api', googleMapsApi.value)
    }

    // Handle Cookiebot on accept: if user clicks on accept preferences, load Google Maps
    window?.addEventListener('CookiebotOnAccept', handleCookiebotAccept, false)

    // Watch for changes to `targetIsVisible` and use geoLocation browser api to set marker position
    watch(
        targetIsVisible, () => {
            if (targetIsVisible.value && !stopWatcher.value) {
                setInitialLocation()
            }
        },
        { immediate: true }
    )
})

// Cleanup on component unmount
onUnmounted(() => {
    // Remove global event listeners
    window.removeEventListener('CookiebotOnAccept', handleCookiebotAccept)

    // Clear Google Maps listeners for specific instances
    if (map) {
        google.maps.event.clearInstanceListeners(map)
    }

    // Clear draggable marker instances
    deleteDraggableMarker()

    // Clear listeners for polygons
    polygons.forEach((polygon) => {
        google.maps.event.clearInstanceListeners(polygon)
        polygon.setMap(null) // Remove polygon from the map
    })
    polygons = [] // Reset polygon array

    // Optionally clear the map instance if needed
    if (map) {
        map = null // Release reference to the map instance
    }
})

// Define emit events
const emit = defineEmits(['update:modelValue'])
</script>

<template>
    <div ref="mapContainer" class="map-wrapper" :class="isPendingMap ? 'pending-map' : ''">
        <LoadingSpinner v-if="isPendingMap" class="container-loader" />

        <!-- Checks if script is loaded and consent preferences are available -->
        <template v-if="cookieConsent">
            <div
                role="button"
                class="cookieconsent-optout-preferences" @click="enableMarketingPreferences"
            >
                <div class="cookie-consent-container ">
                    <div class="cookie-consent">
                        <Pin />
                        <p v-html="t('form.cookie_text')" />
                    </div>
                </div>
            </div>

            <div class="cookieconsent-optin-preferences">
                <div class="map-container">
                    <div id="map" ref="mapDiv" class="map" />

                    <FormText
                        class="map-control-input"
                        :input-config="{ ...googleMapsConfig.setMarkerConfig, val: geoLocationValue, isError: errorAlert?.type === 'location' }"
                        @update:input-value="setGeoLocation"
                        @keydown="suppressFormSubmit"
                    />

                    <div class="map-cta-container">
                        <button
                            class="draw-polygon-cta"
                            :class="getDrawPolygonClass"
                            :title="getDrawPolygonLabel"
                            @click="handleDrawPolygon"
                        >
                            <Transition name="fade" mode="out-in">
                                <Polygon v-if="polygonCoordinates?.length < 1" />
                                <DeletePolygon v-else />
                            </Transition>
                        </button>
                        <button
                            :class="isPanActive? 'pan-to-cta-active' : ''"
                            class="pan-to-cta"
                            :title="t('form.form_last_seen_geometry_pan_to_label')"
                            @click="handleCustomPanTo"
                        >
                            <Pan />
                        </button>
                    </div>
                </div>
            </div>
        </template>
        <!-- If - for what reasons ever, browser does not load cookie script -->
        <ClientOnly v-else>
            <div class="no-cookie-consent-container">
                <div class="no-cookie-consent">
                    <div class="blocked-icon">
                        <Pin />
                    </div>

                    <p v-html="t('form.cookie_blocked_text')" />
                </div>
            </div>
        </ClientOnly>
    </div>
</template>

<style lang="scss">
.map-wrapper {
    margin-block: px2rem(40);
    position: relative;

    &.pending-map {
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgb(8 27 46 / 70%);
            pointer-events: none;
            z-index: z('map-wrapper-overlay');
        }
    }
}

.cookie-consent-container,
.no-cookie-consent-container{
    position: relative;
    background: $map-container-bg;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 800px;
    min-height: 400px;
    max-height: 80vh;
}

.cookie-consent-container {
    cursor: pointer;
}

.cookie-consent {
    border-radius: 10px;
    width: 100%;
    text-align: center;
    padding-inline: px2rem(40);

    &:hover {
        svg {
            transform: scale(1.1);
        }
    }

    svg {
        width: 150px;
        height: 150px;
        fill: currentcolor;
        transition: transform 0.3s ease-in-out;
    }

    p {
       font-size: px2rem(14);
        max-width: 400px;
        margin: px2rem(40) auto;
    }
}

.no-cookie-consent {
    border-radius: 10px;
    width: 100%;
    text-align: center;
    padding-inline: px2rem(40);

        svg {
            width: 150px;
            height: 150px;
            fill: currentcolor;
            opacity: 0.2;
        }

    p {
        font-size: px2rem(14);
        max-width: 400px;
        margin: px2rem(40) auto;
    }
}

.blocked-icon {
    width: 150px;
    height: 150px;
    margin: auto;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

   &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 2px;
        height: 200%;
        background-color: $blocked-icon;
        transform: rotate(45deg);
        transform-origin: bottom left;
    }
}

.map-container {
    position: relative;
    background: $map-container-bg;
    border-radius: 10px;
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        height: 30%;
        background: $map-container-gradient;
        pointer-events: none;
    }
}

.map {
    height: 800px;
    min-height: 400px;
    max-height: 80vh;
    overflow: hidden;
    border-radius: 10px;
}

.map-control-input {
    position: absolute;
    top: 20px;
    left: 20px;
    right: 20px;
    z-index: z('map-control-input');

    @include bp(medium1) {
        top: 40px;
        left: 15%;
        right: 15%;
    }
}

.map-cta-container {
    position: absolute;
    bottom: 36px;
    right: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.pan-to-cta  {
    width: 50px;
    height: 50px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    background-color: $pan-to-cta-bg;
    transition: background-color 0.4s ease-in-out;
    pointer-events: none;
    appearance: none;
    border: 0 none;
    color: inherit;

    @include bp(medium1) {
        width: 60px;
        height: 60px;
    }

    &.pan-to-cta-active {
        background-color: $pan-to-cta-active-bg;
        pointer-events: all;
    }

    svg {
        width: 40px;
        height: 40px;
    }
}

.draw-polygon-cta {
    width: 50px;
    height: 50px;
    background-color: $draw-polygon-cta-bg;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    transition: background-color 0.4s ease-in-out;
    appearance: none;
    border: 0 none;
    color: inherit;

    @include bp(medium1) {
        width: 60px;
        height: 60px;
    }

    &.animated-draw-polygon-cta {
        animation: draw-polygon-cta 2s ease-in-out infinite;
        background-color: $animated-draw-polygon-cta-bg;

        svg {
            animation: draw-polygon-svg 2s ease-in-out infinite;
        }
    }

    &:hover {
        background-color: $draw-polygon-cta-hover-bg;
    }

    &.delete-polygon-cta {
        background-color: $delete-polygon-cta-bg;

        @media (hover: hover) {
            &:hover {
                background-color: $delete-polygon-cta-hover-bg;
            }
        }
    }

    &:focus,
    &:active,
    &.draw-polygon-cta-active {
        background-color: $draw-polygon-cta-active-bg;
    }

    svg {
        width: 40px;
        height: 40px;
    }
}

@keyframes draw-polygon-svg {
    0% {
        transform: scale(0.9);
    }

    50% {
        transform: scale(1);
    }

    100% {
        transform: scale(0.9);
    }
}

@keyframes draw-polygon-cta {
    0% {
    opacity: 1;
    }

    50% {
          opacity: 0.9;
    }

    100% {
        opacity: 1;
    }
}

// Polygon drawing vertex dots (AdvancedMarkerElement content)
.polygon-vertex {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #00b2fb;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgb(0 0 0 / 30%);

    &-first {
        width: 14px;
        height: 14px;
        cursor: pointer;

        &:hover {
            background: #0095d4;
        }
    }
}
</style>
